import { computed, effect, inject, Signal, signal } from '@angular/core';
import {
  Membership,
  HistoryService,
  SensorService,
  UserRole,
  VisualizationGroup,
  Sensor,
} from '@net3-multiplatform-client/libs';
import { format, addDays } from 'date-fns';
import { SensorGroupType } from 'libs/domain/sensor/sensor.enums';

export abstract class HomeBase {
  //@ el component fill ha de proveir membership per al computed i l'effect
  protected abstract membership: Signal<Membership | null>;

  private historyService = inject(HistoryService);
  private sensorsService = inject(SensorService);

  //@ acumulat d'avui carregat via HistoryService
  todayAccumulated = signal<number | null>(null);
  loadingAccumulated = signal(true);

  //@ grups de sensors amb sensors (els buits s'exclouen)
  visualizationGroups = signal<VisualizationGroup[]>([]);
  groupSensorsMap = signal<Record<number, Sensor[]>>({});
  loadingGroupSensors = signal(false);
  expandedGroupIds = signal<number[]>([]);

  isGestor = computed(() => {
    const role = this.membership()?.role;
    return role === UserRole.GESTOR || role === UserRole.ADMIN;
  });

  constructor() {
    //@ carrega acumulat d'avui i grups de sensors quan canvia la comunitat
    effect(() => {
      const membership = this.membership();
      if (!membership) return;
      const today = new Date();
      const from = format(today, 'yyyy-MM-dd');
      const to = format(addDays(today, 1), 'yyyy-MM-dd');
      this.loadingAccumulated.set(true);

      const allSectors = 0; // sense filtre de sector retorna tots
      const firstPage = 1;
      const maxPageSize = 150; // límit hardcode backend per agroupation=TOTAL
      const agroupationTotal = 2; // 0=hora, 1=dia, 2=total acumulat del període
      this.historyService
        .getSectorHistory(
          membership.schema_id,
          allSectors,
          firstPage,
          maxPageSize,
          from,
          to,
          agroupationTotal
        )
        .subscribe({
          next: (response) => {
            const total = response.results.reduce(
              (sum, row) => sum + row.value,
              0
            );
            this.todayAccumulated.set(Math.round(total * 10) / 10);
            this.loadingAccumulated.set(false);
          },
          error: () => {
            this.todayAccumulated.set(null);
            this.loadingAccumulated.set(false);
          },
        });

      this.visualizationGroups.set([]);
      this.groupSensorsMap.set({});
      this.expandedGroupIds.set([]);
      this.loadingGroupSensors.set(true);

      this.sensorsService
        .getSensorsVisualizationGroups(membership.schema_id)
        .subscribe({
          next: (res) => {
            const groups = res.data;
            if (groups.length === 0) {
              this.loadingGroupSensors.set(false);
              return;
            }
            // Una sola crida per tots els grups; s'agrupen els sensors client-side per groupId
            this.sensorsService
              .getSensors(membership.schema_id, 1, 1000, {
                groups: groups.map((group) => ({
                  id: group.id,
                  name: group.name,
                  type: SensorGroupType.VISUALIZATION_GROUP,
                })),
              })
              .subscribe({
                next: (result) => {
                  const sensorsMap: Record<number, Sensor[]> = {};
                  result.results.forEach((sensor) => {
                    if (!sensorsMap[sensor.groupId])
                      sensorsMap[sensor.groupId] = [];
                    sensorsMap[sensor.groupId].push(sensor);
                  });
                  const groupsWithSensors = groups.filter(
                    (group) => (sensorsMap[group.id]?.length ?? 0) > 0
                  );
                  this.groupSensorsMap.set(sensorsMap);
                  this.visualizationGroups.set(groupsWithSensors);
                  this.loadingGroupSensors.set(false);
                },
                error: () => this.loadingGroupSensors.set(false),
              });
          },
          error: () => this.loadingGroupSensors.set(false),
        });
    });
  }

  toggleGroup(id: number) {
    const expanded = this.expandedGroupIds();
    this.expandedGroupIds.set(
      expanded.includes(id)
        ? expanded.filter((x) => x !== id)
        : [...expanded, id]
    );
  }

  isGroupExpanded(id: number) {
    return this.expandedGroupIds().includes(id);
  }
}
